import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';

import { useLocation, useRouteMatch, useHistory, Link } from 'react-router-dom';

import { normalize } from 'normalizr';
import { parse, stringify } from 'query-string';

import {
  scrollToTop,
  enableScroll,
  disableScroll,
  getCurrencyTypeInURLFormat,
} from 'utils';
import {
  getCurrenciesItems,
  getCurrenciesFetching,
  getCurrenciesCrossRatesItems,
  getSelectedCurrencies,
  makeGetLocationParamsValues,
} from 'selectors';
import { FETCH_CURRENCIES_INFO_SUCCESS } from 'actionTypes';
import { currenciesInfo as currenciesInfoSchema } from 'schemas';
import { fetchCurrencies } from 'actions';

import ContentSection from 'components/ContentSection';
import CurrenciesSelect from 'components/CurrenciesSelect';
import { AddBankButton, StyledModal } from 'components/AllBanksTable/styles';

import { SectionTitle } from 'components/SectionTitle';

import {
  Wrapper,
  OverflowWrapper,
  TABLE,
  TR,
  TD,
  THEAD,
  FlagList,
  TableScroll,
} from './styles';

const modalStyles = {
  overlay: {
    zIndex: 3000000001,
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
};

const CrossRates = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const match = useRouteMatch();
  const history = useHistory();
  const { locale, intl } = useIntl();

  const getLocationParamsValues = makeGetLocationParamsValues();

  const makeCurrenciesItems = useSelector(getCurrenciesItems);
  const currenciesIsFetching = useSelector(getCurrenciesFetching);

  const crossRatesItems = useSelector(state =>
    getCurrenciesCrossRatesItems(state, { match, location, intl })
  );
  const selectedCurrencies = useSelector(state =>
    getSelectedCurrencies(state, { match, location, intl })
  );
  const { sourceCurrency, targetCurrency } = useSelector(state =>
    getLocationParamsValues(state, { match, location, intl })
  );

  const [showCurrenciesSelect, setShowCurrenciesSelect] = useState(false);
  const [currenciesToShow, setCurrenciesToShow] = useState(selectedCurrencies);

  const currenciesItems = makeCurrenciesItems?.money;

  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  const fetchCurrenciesByScroll = useCallback(
    () => dispatch(fetchCurrencies({ location, match, locale, type: 'money' })),
    [dispatch, fetchCurrencies]
  );

  const handleAddCurrenciesBtnClick = () => {
    setShowCurrenciesSelect(true);
  };

  const handleCurrenciesSelectClose = useCallback(() => {
    enableScroll();

    setShowCurrenciesSelect(false);
  }, [showCurrenciesSelect]);

  const handleCurrenciesSelectChange = useCallback(
    (currenciesList, currenciesInfo) => {
      enableScroll();

      setCurrenciesToShow(currenciesList);
      setShowCurrenciesSelect(false);

      const {
        entities: { currenciesInfo: entities = {} } = {},
        result: ids = [],
      } = normalize(currenciesInfo, currenciesInfoSchema);

      dispatch({
        type: FETCH_CURRENCIES_INFO_SUCCESS,
        payload: { entities, ids },
      });

      history.replace({
        search: stringify({
          ...parse(location.search),
          'currencies-list': currenciesList.map(({ code }) => code).join('-'),
        }),
      });
    },
    [location]
  );

  const currenciesFlag = currency => {
    return (
      <TD key={currency}>
        <FlagList style={{ MozTransform: 'scale(0.505)' }} name={currency} />
        {currency}
      </TD>
    );
  };

  const currenciesFlagsList = selectedCurrencies.map(({ code }) => (
    <Fragment key={code}>{currenciesFlag(code)}</Fragment>
  ));

  const getCrossRateLink = (source, target) => {
    const sourceType = getCurrencyTypeInURLFormat(source.type);
    const targetType = getCurrencyTypeInURLFormat(target.type);

    const sourceCode = source.code.toLowerCase();
    const targetCode = target.code.toLowerCase();

    return `/converter/${sourceCode}${sourceType}-${targetCode}${targetType}/`;
  };

  const getCrossRateValue = (rates, code) => {
    if (!rates || !code) {
      return undefined;
    }

    if (rates[code] != null) {
      return rates[code];
    }

    return rates[code.toUpperCase()];
  };

  const getCrossRate = (source, target) => {
    const ratesValueByBuy = getCrossRateValue(
      crossRatesItems?.buy,
      source.code
    );
    const ratesValueBySell = getCrossRateValue(
      crossRatesItems?.sell,
      target.code
    );

    if (!ratesValueByBuy || !ratesValueBySell) {
      return '-';
    }

    return (
      <Link to={getCrossRateLink(source, target)}>
        {(ratesValueByBuy / ratesValueBySell).toFixed(4)}
      </Link>
    );
  };

  const renderTd = (rowCurrency, colCurrency) => {
    const isDifferent = rowCurrency.code !== colCurrency.code;

    return (
      <TD key={`${rowCurrency.code}-${colCurrency.code}`} bg={isDifferent}>
        {isDifferent ? getCrossRate(rowCurrency, colCurrency) : ''}
      </TD>
    );
  };

  return (
    <ContentSection as="section">
      <SectionTitle>
        <FormattedMessage id="crossRates.title" />
      </SectionTitle>

      {selectedCurrencies.length > 0 && (
        <Wrapper>
          <OverflowWrapper>
            <TableScroll
              style={{ minWidth: `${(selectedCurrencies.length + 1) * 70}px` }}
            >
              <TABLE>
                <THEAD>
                  <TR>
                    <TD />
                    {currenciesFlagsList}
                  </TR>
                </THEAD>

                <tbody>
                  {selectedCurrencies.map(rowCurrency => {
                    return (
                      <TR key={`${rowCurrency.code}-tr`}>
                        <>
                          {currenciesFlag(rowCurrency.code)}

                          {selectedCurrencies.map(colCurrency =>
                            renderTd(rowCurrency, colCurrency)
                          )}
                        </>
                      </TR>
                    );
                  })}
                </tbody>
              </TABLE>
            </TableScroll>
          </OverflowWrapper>
        </Wrapper>
      )}

      <AddBankButton icon="pencil" onClick={handleAddCurrenciesBtnClick}>
        <FormattedMessage id="editCurrenciesList" />
      </AddBankButton>

      <StyledModal
        isOpen={showCurrenciesSelect}
        onRequestClose={handleCurrenciesSelectClose}
        onAfterOpen={disableScroll}
        style={modalStyles}
      >
        {showCurrenciesSelect && (
          <CurrenciesSelect
            selectedCurrencies={currenciesToShow}
            onRequestReset={handleCurrenciesSelectClose}
            onRequestChange={handleCurrenciesSelectChange}
            loading={currenciesIsFetching}
            onScrollEnd={fetchCurrenciesByScroll}
            currencies={currenciesItems}
            sourceCurrency={sourceCurrency}
            targetCurrency={targetCurrency}
          />
        )}
      </StyledModal>
    </ContentSection>
  );
};

export default CrossRates;
